import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  AlertTriangle, FileX, CalendarX, ChevronDown, ChevronUp,
  ExternalLink, CheckCircle2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BillingAlertWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const fetchAlerts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/dashboard/billing-alerts`, {});
      setData(res.data);
    } catch (err) {
      console.error('Billing alerts error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  if (loading) return null;

  // Zero state
  if (!data?.hasAlerts) {
    return (
      <div
        className="flex items-center gap-2.5 px-4 py-3 bg-green-50 border border-green-200 mb-6"
        data-testid="billing-alert-zero-state"
      >
        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
        <span className="text-sm font-medium text-green-800">
          Toutes les factures sont à jour
        </span>
      </div>
    );
  }

  const spotCount = data.spotWithoutInvoice?.count || 0;
  const contractCount = data.contractsWithoutMonthlyInvoice?.count || 0;
  const totalAlerts = spotCount + contractCount;
  const spotAmount = data.spotWithoutInvoice?.totalAmount || 0;

  return (
    <Card className="border border-zinc-200 bg-white mb-6" data-testid="billing-alert-detailed-widget">
      <CardHeader className="pb-2 pt-4 px-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <CardTitle className="text-sm font-semibold text-zinc-900 tracking-tight">
              Alertes Facturation
            </CardTitle>
            <Badge className="bg-amber-100 text-amber-800 border-0 text-xs font-semibold px-2 py-0">
              {totalAlerts}
            </Badge>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded transition-colors"
            data-testid="toggle-billing-alert-widget"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </CardHeader>

      <CardContent className="px-5 pb-4">
        {/* Summary badges */}
        <div className="flex flex-wrap gap-2 mt-1">
          {spotCount > 0 && (
            <Badge className="bg-red-50 text-red-800 border border-red-200 gap-1.5 font-medium text-xs" data-testid="spot-unbilled-badge">
              <FileX className="w-3.5 h-3.5" />
              {spotCount} opération{spotCount > 1 ? 's' : ''} non facturée{spotCount > 1 ? 's' : ''}
              <span className="text-red-600 font-bold ml-1">
                {spotAmount.toLocaleString('fr-FR')} XOF
              </span>
            </Badge>
          )}
          {contractCount > 0 && (
            <Badge className="bg-orange-50 text-orange-800 border border-orange-200 gap-1.5 font-medium text-xs" data-testid="contract-no-invoice-badge">
              <CalendarX className="w-3.5 h-3.5" />
              {contractCount} contrat{contractCount > 1 ? 's' : ''} sans facture ({data.contractsWithoutMonthlyInvoice?.currentMonth})
            </Badge>
          )}
        </div>

        {/* Expanded details */}
        <div
          className="overflow-hidden transition-all duration-300 ease-in-out"
          style={{ maxHeight: expanded ? '600px' : '0', opacity: expanded ? 1 : 0 }}
        >
          <div className="mt-4 space-y-4">
            {/* Spot operations without invoice */}
            {spotCount > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-[0.2em] mb-2">
                  Opérations spot sans facture
                </h4>
                <div className="space-y-1.5">
                  {data.spotWithoutInvoice.operations.slice(0, 10).map((op) => (
                    <div key={op.operationId}
                      className="flex items-center justify-between bg-white border border-zinc-200 px-3 py-2 text-sm hover:bg-zinc-50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/admin/operations/${op.operationId}`)}
                      data-testid={`alert-op-${op.operationId}`}>
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-xs text-zinc-500">{op.operationNumber || op.operationId?.slice(0, 8)}</span>
                        <span className="text-zinc-700 truncate max-w-[200px]">{op.clientName}</span>
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-zinc-300">
                          {op.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-red-700 font-mono text-xs">
                          {op.amount?.toLocaleString('fr-FR')} XOF
                        </span>
                        <ExternalLink className="w-3.5 h-3.5 text-zinc-400" />
                      </div>
                    </div>
                  ))}
                  {spotCount > 10 && (
                    <p className="text-xs text-zinc-500 text-center mt-1">
                      ... et {spotCount - 10} autre{spotCount - 10 > 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Contracts without monthly invoice */}
            {contractCount > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-[0.2em] mb-2">
                  Contrats actifs sans facture ce mois
                </h4>
                <div className="space-y-1.5">
                  {data.contractsWithoutMonthlyInvoice.contracts.map((c) => (
                    <div key={c.contractId}
                      className="flex items-center justify-between bg-white border border-zinc-200 px-3 py-2 text-sm hover:bg-zinc-50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/admin/contracts/${c.contractId}`)}
                      data-testid={`alert-contract-${c.contractId}`}>
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-xs text-zinc-500">{c.reference || c.contractId?.slice(0, 8)}</span>
                        <span className="text-zinc-700 truncate max-w-[200px]">{c.clientName}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-orange-300 text-orange-700">
                          Pas de facture {c.currentMonth}
                        </Badge>
                        <ExternalLink className="w-3.5 h-3.5 text-zinc-400" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default BillingAlertWidget;
